// Initialisation conditionnelle de Sentry + PostHog.
//
// Aucune lib n'est chargée si les variables d'env ne sont pas définies, pour
// rester léger sur les builds de démo. Les paquets sont importés dynamiquement
// pour ne pas faire grossir le bundle initial.
//
// Configuration côté .env.local :
//   VITE_SENTRY_DSN      = https://...@sentry.io/...
//   VITE_POSTHOG_KEY     = phc_...
//   VITE_POSTHOG_HOST    = https://eu.i.posthog.com   (par défaut UE)
// CSP : domaines déjà whitelistés dans vercel.json + public/_headers.

let posthogClient: { capture: (event: string, props?: Record<string, unknown>) => void; identify: (id: string, props?: Record<string, unknown>) => void; reset: () => void } | null = null;

export async function initObservability(): Promise<void> {
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
  const posthogKey = import.meta.env.VITE_POSTHOG_KEY;

  if (sentryDsn) {
    try {
      const Sentry = await import("@sentry/browser");
      Sentry.init({
        dsn: sentryDsn,
        tracesSampleRate: 0.1,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,
        environment: import.meta.env.MODE,
      });
    } catch (e) {
      console.warn("[observability] Sentry non chargé (lib absente). `npm i @sentry/browser` pour activer.", e);
    }
  }

  if (posthogKey) {
    try {
      const { default: posthog } = await import("posthog-js");
      posthog.init(posthogKey, {
        api_host: import.meta.env.VITE_POSTHOG_HOST ?? "https://eu.i.posthog.com",
        person_profiles: "identified_only",
        autocapture: false,
        capture_pageview: true,
        disable_session_recording: true,
      });
      posthogClient = posthog;
    } catch (e) {
      console.warn("[observability] PostHog non chargé. `npm i posthog-js` pour activer.", e);
    }
  }
}

export function track(event: string, props?: Record<string, unknown>) {
  posthogClient?.capture(event, props);
}

export function identify(userId: string, props?: Record<string, unknown>) {
  posthogClient?.identify(userId, props);
}

export function resetAnalytics() {
  posthogClient?.reset();
}

export async function captureException(err: unknown, context?: Record<string, unknown>): Promise<void> {
  try {
    const Sentry = await import("@sentry/browser");
    Sentry.captureException(err, { extra: context });
  } catch {
    // Sentry pas chargé — fallback console
    console.error("[captureException]", err, context);
  }
}
